import 'leaflet/dist/leaflet.css';
import type { GuardRouteDetail } from '@yawi/types';
import type { LatLngTuple } from 'leaflet';
import { CircleMarker, MapContainer, Polyline, TileLayer, Tooltip } from 'react-leaflet';
import { useI18n } from '@/shared/i18n';
import { formatDuration } from '../routeFormat';

const marker = (fillColor: string) => ({ color: '#fff', weight: 2, fillColor, fillOpacity: 1 });

export function RouteMap({ route }: { route: GuardRouteDetail }) {
  const { t } = useI18n();
  // Igual que el resumen del servidor (route-summary.ts, MAX_ACCURACY_M): las lecturas con
  // precisión > 50 m se guardan pero no se dibujan, si no el trazo salta decenas de metros.
  const positions = route.points
    .filter((p) => p.accuracy == null || p.accuracy <= 50)
    .map((p): LatLngTuple => [p.latitude, p.longitude]);
  const start = positions[0];
  const end = positions[positions.length - 1];

  if (!start) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-slate-500">
        {t('guardRoutes.noPoints')}
      </div>
    );
  }

  return (
    // key: MapContainer ignora cambios de props tras montarse; se remonta para encuadrar otro recorrido.
    <MapContainer
      key={route.id}
      bounds={positions.length > 1 ? positions : undefined}
      center={positions.length > 1 ? undefined : start}
      zoom={17}
      boundsOptions={{ padding: [32, 32] }}
      className="h-full w-full"
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <Polyline positions={positions} pathOptions={{ color: '#7c3aed', weight: 5 }} />
      {route.stops.map((stop) => (
        <CircleMarker
          key={stop.startedAt}
          center={[stop.latitude, stop.longitude]}
          radius={7}
          pathOptions={marker('#f97316')}
        >
          <Tooltip>
            {t('guardRoutes.stop')} · {formatDuration(stop.durationSeconds)}
          </Tooltip>
        </CircleMarker>
      ))}
      <CircleMarker center={start} radius={8} pathOptions={marker('#16a34a')}>
        <Tooltip>{t('guardRoutes.start')}</Tooltip>
      </CircleMarker>
      {route.status === 'COMPLETED' && (
        <CircleMarker center={end} radius={8} pathOptions={marker('#dc2626')}>
          <Tooltip>{t('guardRoutes.end')}</Tooltip>
        </CircleMarker>
      )}
    </MapContainer>
  );
}
